import { ApiService } from "@/api";
import {
  DocumentsToSignResponse,
  DocumentToSign,
  initSignDocumentPageAction,
  PageMode,
} from "./signDocumentDomain";
import { makeAutoObservable } from "mobx";
import { TMessage } from "@/i18n/tt";
import UserStore from "@/stores/UserStore";
import { SigningMethod } from "@/common/common";
import { EventDispatcher } from "@/events/eventsDomain";
import { RootStoreData } from "@/events/rootStoreData";

export class BatchSigningController {
  batchDocuments: DocumentToSign[];
  selectedDocumentIndex = 0;
  batchId: string | undefined;
  pageMode: PageMode = "multi";

  constructor(
    readonly userStore: UserStore,
    readonly apiService: ApiService,
    readonly dispatch: EventDispatcher,
    readonly rootStoreData: RootStoreData,
    readonly signingMethod: SigningMethod,
    readonly returnUrl: string,
  ) {
    this.batchDocuments = [];
    makeAutoObservable(
      this,
      { apiService: false, dispatch: false, rootStoreData: false },
      { autoBind: true },
    );
  }

  get signingDetails(): DocumentToSign | undefined {
    return this.rootStoreData.signDocument?.signingDetails;
  }

  *init(returnUrl: string, documentReference?: string, batchId?: string, mode?: string) {
    this.pageMode = mode === "single" ? "single" : "multi";
    if (batchId) {
      this.batchId = batchId;
      yield this.fetchBatchDocuments(batchId);
    } else {
      yield this.getDocumentsToSign();
      if (this.batchDocuments.length > 1) {
        this.batchId = yield this.apiService.createSigningDocumentBatch(
          this.batchDocuments.map((d) => d.documentReference),
        );
      }
    }
    if (documentReference) {
      this.dispatch(
        initSignDocumentPageAction(documentReference, this.batchId, returnUrl, this.pageMode),
      );
      const index = this.batchDocuments.findIndex((d) => d.documentReference === documentReference);
      this.selectedDocumentIndex = index > 0 ? index : 0;
    } else {
      if (this.selectedDocument) {
        this.dispatch(
          initSignDocumentPageAction(
            this.selectedDocument.documentReference,
            this.batchId,
            returnUrl,
          ),
        );
      }
    }
  }

  *fetchBatchDocuments(batchId: string) {
    const response: DocumentsToSignResponse =
      yield this.apiService.getSigningBatchDocuments(batchId);
    this.batchDocuments = response.documents;
  }

  *getDocumentsToSign() {
    const response: DocumentsToSignResponse = yield this.apiService.getDocumentsToSign();
    this.batchDocuments = response.documents.filter((d) => d.signingMethod === this.signingMethod);
  }

  fetchDocument() {
    if (this.selectedDocument) {
      this.dispatch(
        initSignDocumentPageAction(
          this.selectedDocument.documentReference,
          this.batchId,
          this.returnUrl,
        ),
      );
    }
  }

  get showNavigationButtons() {
    return this.signingDetails && this.batchDocuments.length > 1 && !(this.pageMode === "single");
  }

  get selectedDocument(): DocumentToSign | undefined {
    return this.batchDocuments[this.selectedDocumentIndex];
  }

  nextDocument() {
    if (this.selectedDocumentIndex < this.numberOfSignaturesForMethod - 1) {
      this.selectedDocumentIndex++;
      this.fetchDocument();
    }
  }

  previousDocument() {
    if (this.selectedDocumentIndex > 0) {
      this.selectedDocumentIndex--;
      this.fetchDocument();
    }
  }

  get numberOfSignaturesForMethod() {
    return this.batchDocuments.length;
  }

  get readyHeader(): TMessage | undefined {
    if (!this.signingDetails) return undefined;
    if (this.pageMode === "single") {
      return {
        key: "signingDialog.readyHeader.single",
        params: { title: this.signingDetails.documentTitle },
      };
    }
    return {
      key: "signingDialog.readyHeader.multi",
      params: { index: this.selectedDocumentIndex + 1, count: this.numberOfSignaturesForMethod },
    };
  }

  get isLoading() {
    return this.signingDetails === undefined;
  }

  get showSuccess(): boolean {
    return this.rootStoreData.signDocument?.type === "signed";
  }

  selectDocument(document: DocumentToSign) {
    this.selectedDocumentIndex = this.batchDocuments.indexOf(document) ?? 0;
    this.fetchDocument();
  }
}
